import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { User } from '../types';
import { useOutsideNavigation, useReactReady } from '../hooks';

export type ChangeUserContextType = {
  user: User | null;
  isLoading: boolean;
  error: string | null;
};

const UserContext = createContext<ChangeUserContextType>({
  user: null,
  isLoading: true,
  error: null,
});

export const USER_EVENT = 'userStateChange';

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useOutsideNavigation();

  useEffect(() => {
    const handleUserChange = (event: CustomEvent<ChangeUserContextType>) => {
      setUser(event.detail.user || null);
      setIsLoading(event.detail.isLoading);
      setError(event.detail.error);
    };

    // Listen for custom events
    window.addEventListener(USER_EVENT, handleUserChange as EventListener);

    // Signal that React is ready to receive events
    return () => {
      window.removeEventListener(USER_EVENT, handleUserChange as EventListener);
    };
  }, []);

  useReactReady();

  const value = useMemo(
    () => ({ user, isLoading, error }),
    [user, isLoading, error],
  );

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
